<template>
  <transition name="modal" appear>
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      @click.self="$emit('close')"
    >
      <div class="flex w-full max-w-md flex-col overflow-hidden rounded-xl bg-surface">
        <div class="flex flex-col gap-6 p-6">
          <!-- Header -->
          <div class="flex items-start justify-between">
            <h2 class="text-content text-xl font-bold tracking-tight">{{ $t('login.qrLogin.title') }}</h2>
            <button
              @click="$emit('close')"
              class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-muted transition-colors hover:bg-content/10 hover:text-content"
            >
              <span class="material-symbols-outlined text-2xl">close</span>
            </button>
          </div>

          <!-- Content -->
          <div class="flex flex-col items-center gap-4 py-4">
            <!-- QR Code -->
            <div class="flex items-center justify-center w-full min-h-[200px]">
              <img
                v-if="!qrVisible"
                src="@/assets/images/icon/iconClose.png"
                class="w-[150px] opacity-50"
                alt="Expired"
              >
              <vue-qrcode
                v-if="qrVisible"
                :value="qrTargetText"
                :options="qrOption"
                tag="img"
                class="rounded-lg"
              ></vue-qrcode>
            </div>

            <!-- Timer -->
            <div class="text-center">
              <p class="text-content text-lg">
                {{ $t('login.qrLogin.timeRemaining') }} 
                <span class="text-green-400 font-bold">{{ qrTimetext }}</span>
              </p>
            </div>

            <!-- Message -->
            <div class="rounded-lg border dark:border-roundBorder bg-base p-4 w-full">
              <div v-if="qrVisible" class="text-content text-sm text-center leading-relaxed">
                {{ $t('login.qrLogin.description1') }}<br>
                {{ $t('login.qrLogin.description2') }} <br>
                {{ $t('login.qrLogin.description3') }}<br>
                <span class="text-muted">
                  {{ $t('login.qrLogin.description4') }}<br>
                  {{ $t('login.qrLogin.description5') }}
                </span>
              </div>
              <div v-if="!qrVisible" class="flex flex-col items-center gap-4">
                <p class="text-content text-sm text-center leading-relaxed">
                  {{ $t('login.qrLogin.description6') }}<br>
                  {{ $t('login.qrLogin.description7') }}
                </p>
                <button
                  @click="onPressedBtnRetryLoginQR"
                  class="flex items-center justify-center rounded-lg h-10 px-6 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
                >
                  <span class="material-symbols-outlined mr-2">refresh</span>
                  {{ $t('login.qrLogin.btnRetry') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import axios from 'axios'
  import {CustomAlert} from '@/assets/js/util.js';
  import VueQrcode from "@chenfengyuan/vue-qrcode";
  import { AUTH_URL } from '@/config';

  export default {
    components: {
      VueQrcode
    },
    data() {
      return {
        isDuplicationLoginCheck: false,

        checkTimer: null,
        qrTimetext: "",
        qrTimeMax: 120,
        qrTargetText: "QRText",
        qrVisible: true,
        qrOption: {
          errorCorrectionLevel: "M",
          maskPattern: 0,
          margin: 4,
          scale: 2,
          width: 200,
          color: {
            dark: "#000000FF",
            light: "#FFFFFFFF"
          }
        }
      };
    },
    created(){
      this.getLoginQRCode();
      this.loginQrCodeCheckTimer();
    },
    beforeUnmount(){
      if(this.checkTimer != null){
        clearInterval(this.checkTimer);
        this.checkTimer = null;
      }
    },
    methods:{
      // SSO access_token을 Bearer로 전달 (Auth QR API는 JWT 인증 필요)
      authConfig() {
        return {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        };
      },

      // Get QR-Code for login with QR-Code (Auth 서버에서 qr_token 발급)
      async getLoginQRCode() {
        const res = await axios.post(`${AUTH_URL}/qr/generate`, {}, this.authConfig());
        const qrToken = res.data.qr_token;

        if(qrToken !== undefined){
          this.qrTargetText = qrToken;
          this.startQRTimer();
        }
      },

      // QR-Code timer (MAX = 2Min)
      async startQRTimer() {
        this.qrVisible = true;
        this.qrTimetext = "02m 00s";
        
        let temp = this.qrTimeMax;
        const timer = setInterval(() => {
          if (temp > 0) {
            temp--;
            
            let minutes = Math.floor(temp / 60);
            let seconds = temp % 60;
            
            this.qrTimetext = minutes.toString().padStart(2, '0') + "m ";
            this.qrTimetext += seconds.toString().padStart(2, '0') + "s";
          } else {
            clearInterval(timer);
            this.qrVisible = false;
            this.qrTimetext = "Times Up";

            // send expire event
            this.setLoginQRCodeUseYnStatus();
          }
        }, 1000);
      },

      // QR 타이머 종료 시 수동 만료 처리
      async setLoginQRCodeUseYnStatus(){
        await axios.post(`${AUTH_URL}/qr/expire`, {}, this.authConfig());
      },
      
      // Restart Login with QR-Code
      onPressedBtnRetryLoginQR(){
        this.getLoginQRCode();
      },

      // Check UseYn of login with QR-Code - Timer
      loginQrCodeCheckTimer(){
        this.checkTimer = setInterval(() => {
          if(this.isDuplicationLoginCheck){
            clearInterval(this.checkTimer);
            this.checkTimer = null;
          }

          this.getLoginQRCodeCheckUseYnStatus();
        }, 2000);
      },

      // Check status of login with QR-Code (Auth 서버 폴링)
      async getLoginQRCodeCheckUseYnStatus() {
        const res = await axios.get(`${AUTH_URL}/qr/status`, this.authConfig());
        if(res.data.status === 'DONE'){
          this.isDuplicationLoginCheck = true;
          CustomAlert(
            "다른 기기에서 같은 계정으로 로그인되어 종료됩니다.",
            false,
            ()=>{ this.$store.dispatch('logout'); },
            ()=>{ this.$store.dispatch('logout'); },
          );
        }
      },
    },
  };
</script>

<style scoped>
/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from > div,
.modal-leave-to > div {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
